import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { type Audience } from "features/mediaDataRoom/models";
import { logError } from "utils";
import {
  AudienceSizeEstimationError,
  type AudienceSizesQueryResult,
  type AudienceSizesStateHookResult,
  type DisableSizeEstimationForAudienceFn,
} from "./models";

interface AudienceSizesStateHookPayload {
  enableAudienceSizeEstimation: string[];
  setEnableAudienceSizeEstimation: Dispatch<SetStateAction<string[]>>;
  audiences: Audience[] | undefined;
  audienceSizes: AudienceSizesQueryResult;
}

const useAudienceSizesState = ({
  audiences,
  audienceSizes,
  enableAudienceSizeEstimation,
  setEnableAudienceSizeEstimation,
}: AudienceSizesStateHookPayload): AudienceSizesStateHookResult => {
  useEffect(() => {
    setEnableAudienceSizeEstimation((currentIds) => {
      const currentIdsWithoutStale = currentIds.filter((audience) =>
        audiences?.some((a) => a.id === audience)
      );
      if (currentIdsWithoutStale.length !== currentIds.length) {
        return currentIdsWithoutStale;
      }
      return currentIds;
    });
  }, [audiences, setEnableAudienceSizeEstimation]);
  const enableSizeEstimationForAudience = useCallback(
    (audienceId: string) => {
      setEnableAudienceSizeEstimation((currentIds) =>
        currentIds.includes(audienceId)
          ? currentIds
          : [...currentIds, audienceId]
      );
    },
    [setEnableAudienceSizeEstimation]
  );
  // TODO: handle case when audience is deleted and there is a running query for it we abort the query
  const disableSizeEstimationForAudience =
    useCallback<DisableSizeEstimationForAudienceFn>(
      (id) =>
        setEnableAudienceSizeEstimation((currentIds) => {
          const ids = Array.isArray(id) ? id : [id];
          const filteredIds = currentIds.filter(
            (currentId) => !ids.includes(currentId)
          );
          if (filteredIds.length !== currentIds.length) {
            return filteredIds;
          }
          return currentIds;
        }),
      [setEnableAudienceSizeEstimation]
    );
  return useMemo<AudienceSizesStateHookResult>(
    () => ({
      audienceSizes: enableAudienceSizeEstimation.reduce((acc, id) => {
        const error = audienceSizes.find(
          ({ error }) =>
            error &&
            error instanceof AudienceSizeEstimationError &&
            error.audienceId === id
        )?.error as AudienceSizeEstimationError | undefined;
        if (error) {
          logError(error);
        }
        const data = audienceSizes.find(
          ({ data }) => data && Object.hasOwn(data, id)
        )?.data;
        if (data) {
          return {
            ...acc,
            [id]: { audienceSize: data[id], error: null, loading: false },
          };
        }
        return {
          ...acc,
          [id]: {
            audienceSize: null,
            error: error?.originalError ?? null,
            loading: !error,
          },
        };
      }, {}),
      disableSizeEstimationForAudience,
      enableSizeEstimationForAudience,
    }),
    [
      enableSizeEstimationForAudience,
      disableSizeEstimationForAudience,
      enableAudienceSizeEstimation,
      audienceSizes,
    ]
  );
};

export default useAudienceSizesState;
